import { KeyValue } from '@angular/common'
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { OccConfig } from '@spartacus/core'
import { BehaviorSubject, map, Observable, of, Subscription, tap } from 'rxjs'
import { baseSiteToCountry } from '../../../services/custom-context-service.service'
import { ProductDetailsService } from '../../../services/product.service'
import { SharedService } from '../../../services/shared/shared.service'
import { CustomSearchInputComponent } from '../../shared/custom-components/custom-search-input/custom-search-input.component'
import { CustomTableColumnType } from '../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../shared/custom-components/custom-table/modals/interface/custom-table.interface'
import { BrandYearQuarterMap, ProductUpdate } from './product-updates.model'

@Component({
  selector: 'app-product-updates',
  templateUrl: './product-updates.component.html',
  styleUrl: './product-updates.component.scss',
  standalone: false
})
export class ProductUpdatesComponent implements OnInit, OnDestroy {
  @ViewChild(CustomSearchInputComponent) searchInputComponent!: CustomSearchInputComponent

  private subscription = new Subscription()
  public showInitialLoader$ = new BehaviorSubject(true)
  public inputSearch: string
  public initialQuarterlyProductUpdates: BrandYearQuarterMap
  public quarterlyProductUpdates$: Observable<BrandYearQuarterMap>
  public currentCountry: string
  public headers: ICustomTableHeader[] = [
    { title: 'productUpdates.changeType' },
    { title: 'productUpdates.productCode' },
    { title: 'productUpdates.productName' },
    { title: 'productUpdates.replacementCode' },
    { title: 'productUpdates.esitmatedTime' },
    { title: 'productUpdates.downloadPDF' }
  ]
  public columns: ICustomTableColumn[] = [
    { key: 'changeType' },
    { key: 'productCode' },
    { key: 'productName' },
    { key: 'replacementCode' },
    { key: 'esitmatedTime' },
    {
      key: 'downloadPDF',
      type: CustomTableColumnType.MULTIPLE_LINKS
    }
  ]
  public expandedPath: {
    brand: string
    year: string
    quarter: string
  } | null = null

  constructor(
    private sharedService: SharedService,
    private productService: ProductDetailsService,
    public occConfig: OccConfig
  ) {
    this.currentCountry = baseSiteToCountry(this.sharedService.baseSiteId).toUpperCase() //Keys are in uppercase
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  ngOnInit(): void {
    this.quarterlyProductUpdates$ = this.productService.getProductUpdates(this.currentCountry).pipe(
      map((productUpdates) => this.groupByBrandYearQuarter(productUpdates)),
      tap((quarterlyProductUpdates) => {
        this.initialQuarterlyProductUpdates = quarterlyProductUpdates
        this.showInitialLoader$.next(false)
      })
    )
  }

  public onSearch(value): void {
    if (!value) {
      this.quarterlyProductUpdates$ = of(this.initialQuarterlyProductUpdates)
      this.expandedPath = null
    } else {
      let selectedSearch = value?.trim()
      let searchResult = this.findQuarterInformationByProductCode(this.initialQuarterlyProductUpdates, selectedSearch)
      this.quarterlyProductUpdates$ = of(searchResult)
      this.openSearchResultAccordion(searchResult)
    }
  }

  public brandOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => {
    const order = ['TORK', 'TENA', 'LIBRA']
    return order.indexOf(a.key.toUpperCase()) - order.indexOf(b.key.toUpperCase())
  }

  public yearOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => {
    return Number(b.key) - Number(a.key)
  }

  public quarterOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => {
    const order = ['Q4', 'Q3', 'Q2', 'Q1']
    return order.indexOf(a.key.toUpperCase()) - order.indexOf(b.key.toUpperCase())
  }

  private openSearchResultAccordion(searchResult): void {
    if (Object.keys(searchResult).length) {
      const brand = Object.keys(searchResult)[0]
      const year = Object.keys(brand)[0]
      const quarter = Object.keys(year)[0]

      this.expandedPath = { brand, year, quarter }
    }
  }

  private groupByBrandYearQuarter(list: ProductUpdate[]): BrandYearQuarterMap {
    return list.reduce((acc, item) => {
      const { brand, year, quarter } = item

      if (!acc[brand]) {
        acc[brand] = {}
      }

      if (!acc[brand][year]) {
        acc[brand][year] = {}
      }

      if (!acc[brand][year][quarter]) {
        acc[brand][year][quarter] = []
      }

      acc[brand][year][quarter].push(item)

      return acc
    }, {} as BrandYearQuarterMap)
  }

  private findQuarterInformationByProductCode(records: any, searchInput: string): BrandYearQuarterMap {
    return Object.entries(records).reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        const filteredArray = value.filter((item) => item.productCode === searchInput)
        if (filteredArray.length > 0) {
          acc[key] = filteredArray
        }
      } else if (typeof value === 'object' && value !== null) {
        const filteredObj = this.findQuarterInformationByProductCode(value, searchInput)
        if (Object.keys(filteredObj).length > 0) {
          acc[key] = filteredObj
        }
      }
      return acc
    }, {})
  }
}
